/**
 * useNetworkStatus Hook
 * 
 * Centralized hook for tracking online/offline status with pending write queue integration.
 * Provides reactive state for UI components to show connectivity and sync status.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getQueueStats,
  onQueueEvent,
  flushQueue,
  isFlushInProgress,
} from '@/lib/offlineQueue';
import { firestorePersistenceEnabled, firestorePersistenceError } from '@/lib/firebase';

export interface NetworkStatus {
  /** Whether the browser reports being online */
  isOnline: boolean;
  /** Whether we were previously offline (for showing "reconnected" message) */
  wasOffline: boolean;
  /** Number of pending writes in the offline queue */
  pendingWriteCount: number;
  /** Whether the queue is currently being flushed */
  isSyncing: boolean;
  /** Pending write categories for detailed status */
  pendingCategories: Record<string, number>;
  /** Whether Firestore offline persistence is enabled */
  isPersistenceEnabled: boolean;
  /** Error if persistence failed (e.g., Safari private mode) */
  persistenceError: string | null;
  /** Manually trigger a queue flush */
  triggerSync: () => Promise<void>;
}

/**
 * Hook to track network connectivity and offline queue status
 */
export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(() => 
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);
  const [pendingWriteCount, setPendingWriteCount] = useState(0);
  const [pendingCategories, setPendingCategories] = useState<Record<string, number>>({});
  const [isSyncing, setIsSyncing] = useState(false);

  // Refresh queue stats
  const refreshStats = useCallback(async () => {
    try {
      const stats = await getQueueStats();
      setPendingWriteCount(stats.pendingCount);
      setPendingCategories(stats.categories);
    } catch (err) {
      console.warn('[useNetworkStatus] Failed to get queue stats:', err);
    }
  }, []);

  // Manual sync trigger
  const triggerSync = useCallback(async () => {
    if (!isOnline || isSyncing) return;
    setIsSyncing(true);
    try {
      await flushQueue();
    } finally {
      setIsSyncing(false);
      await refreshStats();
    }
  }, [isOnline, isSyncing, refreshStats]);

  // Online/offline event handlers
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Trigger sync when coming back online
      if (wasOffline) {
        triggerSync();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline, triggerSync]);

  // Queue event listeners
  useEffect(() => {
    const unsubChange = onQueueEvent('change', () => {
      refreshStats();
    });

    const unsubFlushStart = onQueueEvent('flush-start', () => {
      setIsSyncing(true);
    });

    const unsubFlushComplete = onQueueEvent('flush-complete', () => {
      setIsSyncing(false);
      refreshStats();
    });

    // Initial stats fetch
    refreshStats();

    // Also check if a flush is already in progress
    setIsSyncing(isFlushInProgress());

    return () => {
      unsubChange();
      unsubFlushStart();
      unsubFlushComplete();
    };
  }, [refreshStats]);

  return {
    isOnline,
    wasOffline,
    pendingWriteCount,
    isSyncing,
    pendingCategories,
    isPersistenceEnabled: firestorePersistenceEnabled,
    persistenceError: firestorePersistenceError?.message || null,
    triggerSync,
  };
}

